// Order Detection Effectiveness Dashboard
// Displays LLM order detection metrics: approval rates, confidence,
// outcome breakdown, trends, and per-salesperson stats.

import { useState, useEffect, useCallback } from 'react';
import { orderAnalyticsService } from '../../services/orderAnalytics';
import type {
  OrderDetectionOverview,
  OrderDetectionByOutcome,
  ConfidenceDistribution,
  OrderDetectionTrend,
  SalespersonOrderStats,
} from '../../services/orderAnalytics';
import styles from './OrderDetectionDashboard.module.css';

type DateRange = '1m' | '3m' | '6m' | '1y';

function getDateRange(range: DateRange): { from: Date; to: Date } {
  const to = new Date();
  const from = new Date();
  if (range === '1m') from.setMonth(from.getMonth() - 1);
  else if (range === '3m') from.setMonth(from.getMonth() - 3);
  else if (range === '6m') from.setMonth(from.getMonth() - 6);
  else from.setFullYear(from.getFullYear() - 1);
  return { from, to };
}

function formatPct(value: number): string {
  return `${value.toFixed(1)}%`;
}

function formatHours(value: number | null): string {
  if (value === null) return '-';
  if (value < 1) return `${Math.round(value * 60)}m`;
  return `${value.toFixed(1)}h`;
}

export default function OrderDetectionDashboard() {
  const [dateRange, setDateRange] = useState<DateRange>('6m');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [overview, setOverview] = useState<OrderDetectionOverview | null>(null);
  const [outcome, setOutcome] = useState<OrderDetectionByOutcome | null>(null);
  const [confidence, setConfidence] = useState<ConfidenceDistribution[]>([]);
  const [trend, setTrend] = useState<OrderDetectionTrend[]>([]);
  const [salespersons, setSalespersons] = useState<SalespersonOrderStats[]>([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { from, to } = getDateRange(dateRange);

    try {
      const [ov, oc, cf, tr, sp] = await Promise.all([
        orderAnalyticsService.getOverview(from, to),
        orderAnalyticsService.getByOutcome(from, to),
        orderAnalyticsService.getConfidenceDistribution(from, to),
        orderAnalyticsService.getTrend(from, to),
        orderAnalyticsService.getBySalesperson(from, to),
      ]);
      setOverview(ov);
      setOutcome(oc);
      setConfidence(cf);
      setTrend(tr);
      setSalespersons(sp);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          Failed to load analytics: {error}
          <button onClick={loadData} className={styles.retryBtn}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.dashHeader}>
        <h2 className={styles.dashTitle}>LLM Order Detection Effectiveness</h2>
        <div className={styles.dateRangeSelector}>
          {(['1m', '3m', '6m', '1y'] as DateRange[]).map((r) => (
            <button
              key={r}
              className={`${styles.rangeBtn} ${dateRange === r ? styles.rangeBtnActive : ''}`}
              onClick={() => setDateRange(r)}
            >
              {r === '1m' ? '1 Month' : r === '3m' ? '3 Months' : r === '6m' ? '6 Months' : '1 Year'}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className={styles.loading}>Loading analytics...</div>
      ) : (
        <>
          {/* KPI Cards */}
          {overview && (
            <div className={styles.kpiGrid}>
              <div className={styles.kpiCard}>
                <div className={styles.kpiValue}>{overview.total_detected}</div>
                <div className={styles.kpiLabel}>Orders Detected</div>
              </div>
              <div className={`${styles.kpiCard} ${styles.kpiSuccess}`}>
                <div className={styles.kpiValue}>{formatPct(overview.approval_rate)}</div>
                <div className={styles.kpiLabel}>Approval Rate</div>
              </div>
              <div className={`${styles.kpiCard} ${styles.kpiDanger}`}>
                <div className={styles.kpiValue}>{formatPct(overview.rejection_rate)}</div>
                <div className={styles.kpiLabel}>Rejection Rate</div>
              </div>
              <div className={styles.kpiCard}>
                <div className={styles.kpiValue}>
                  {overview.avg_confidence !== null ? formatPct(overview.avg_confidence * 100) : '-'}
                </div>
                <div className={styles.kpiLabel}>Avg Confidence</div>
              </div>
              <div className={styles.kpiCard}>
                <div className={styles.kpiValue}>{formatHours(overview.avg_time_to_approval_hours)}</div>
                <div className={styles.kpiLabel}>Avg Time to Action</div>
              </div>
              <div className={styles.kpiCard}>
                <div className={styles.kpiValue}>{overview.total_pending}</div>
                <div className={styles.kpiLabel}>Pending Review</div>
              </div>
            </div>
          )}

          {/* Outcome Breakdown */}
          {outcome && outcome.total > 0 && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Detection Outcome Breakdown</h3>
              <div className={styles.outcomeGrid}>
                <OutcomeBar
                  label="Approved (no edits)"
                  count={outcome.approved_no_edits}
                  total={outcome.total}
                  color="#10b981"
                />
                <OutcomeBar
                  label="Approved (with edits)"
                  count={outcome.approved_with_edits}
                  total={outcome.total}
                  color="#f59e0b"
                />
                <OutcomeBar
                  label="Rejected"
                  count={outcome.rejected}
                  total={outcome.total}
                  color="#ef4444"
                />
                <OutcomeBar
                  label="Cancelled"
                  count={outcome.cancelled}
                  total={outcome.total}
                  color="#6b7280"
                />
                <OutcomeBar
                  label="Still Active"
                  count={outcome.still_active}
                  total={outcome.total}
                  color="#3b82f6"
                />
              </div>
              {/* Stacked summary bar */}
              <div className={styles.stackedBar}>
                {outcome.approved_no_edits > 0 && (
                  <div
                    className={styles.stackedSegment}
                    style={{
                      width: `${(outcome.approved_no_edits / outcome.total) * 100}%`,
                      background: '#10b981',
                    }}
                    title={`Approved (no edits): ${outcome.approved_no_edits}`}
                  />
                )}
                {outcome.approved_with_edits > 0 && (
                  <div
                    className={styles.stackedSegment}
                    style={{
                      width: `${(outcome.approved_with_edits / outcome.total) * 100}%`,
                      background: '#f59e0b',
                    }}
                    title={`Approved (with edits): ${outcome.approved_with_edits}`}
                  />
                )}
                {outcome.rejected > 0 && (
                  <div
                    className={styles.stackedSegment}
                    style={{
                      width: `${(outcome.rejected / outcome.total) * 100}%`,
                      background: '#ef4444',
                    }}
                    title={`Rejected: ${outcome.rejected}`}
                  />
                )}
                {outcome.cancelled > 0 && (
                  <div
                    className={styles.stackedSegment}
                    style={{
                      width: `${(outcome.cancelled / outcome.total) * 100}%`,
                      background: '#6b7280',
                    }}
                    title={`Cancelled: ${outcome.cancelled}`}
                  />
                )}
                {outcome.still_active > 0 && (
                  <div
                    className={styles.stackedSegment}
                    style={{
                      width: `${(outcome.still_active / outcome.total) * 100}%`,
                      background: '#3b82f6',
                    }}
                    title={`Still Active: ${outcome.still_active}`}
                  />
                )}
              </div>
            </div>
          )}

          {/* Confidence Distribution */}
          {confidence.length > 0 && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Confidence Score Distribution</h3>
              <div className={styles.confidenceGrid}>
                {confidence.map((bucket) => {
                  const maxCount = Math.max(...confidence.map((c) => c.count), 1);
                  return (
                    <div key={bucket.bucket} className={styles.confidenceRow}>
                      <div className={styles.confidenceBucket}>{bucket.bucket}</div>
                      <div className={styles.confidenceBarTrack}>
                        <div
                          className={styles.confidenceBarApproved}
                          style={{ width: `${(bucket.approved_count / maxCount) * 100}%` }}
                        />
                        <div
                          className={styles.confidenceBarRejected}
                          style={{
                            width: `${(bucket.rejected_count / maxCount) * 100}%`,
                            marginLeft: `${(bucket.approved_count / maxCount) * 100}%`,
                          }}
                        />
                      </div>
                      <div className={styles.confidenceCount}>
                        {bucket.count} ({bucket.approved_count}A / {bucket.rejected_count}R)
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className={styles.legendRow}>
                <span className={styles.legendItem}>
                  <span className={styles.legendDot} style={{ background: '#10b981' }} />
                  Approved
                </span>
                <span className={styles.legendItem}>
                  <span className={styles.legendDot} style={{ background: '#ef4444' }} />
                  Rejected
                </span>
              </div>
            </div>
          )}

          {/* Detection Trend */}
          {trend.length > 0 && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Detection Trend</h3>
              <div className={styles.trendGrid}>
                {trend.map((point) => {
                  const maxDetected = Math.max(...trend.map((t) => t.detected), 1);
                  return (
                    <div key={point.period} className={styles.trendRow}>
                      <div className={styles.trendPeriod}>{point.period}</div>
                      <div className={styles.trendBarTrack}>
                        <div
                          className={styles.trendBarFill}
                          style={{ width: `${(point.detected / maxDetected) * 100}%` }}
                        >
                          <span className={styles.trendBarLabel}>
                            {point.detected} detected
                          </span>
                        </div>
                      </div>
                      <div className={styles.trendStats}>
                        <span className={styles.trendApproved}>{point.approved}A</span>
                        <span className={styles.trendRejected}>{point.rejected}R</span>
                        <span className={styles.trendRate}>{formatPct(point.approval_rate)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Salesperson Performance */}
          {salespersons.length > 0 && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>By Salesperson</h3>
              <table className={styles.spTable}>
                <thead>
                  <tr>
                    <th>Salesperson</th>
                    <th>Orders</th>
                    <th>Approved</th>
                    <th>Rejected</th>
                    <th>Rate</th>
                    <th>Avg Action Time</th>
                  </tr>
                </thead>
                <tbody>
                  {salespersons.map((sp) => (
                    <tr key={sp.salesperson_no}>
                      <td>
                        <strong>{sp.salesperson_name}</strong>
                        <br />
                        <span className={styles.spNo}>{sp.salesperson_no}</span>
                      </td>
                      <td>{sp.total_orders}</td>
                      <td className={styles.approved}>{sp.approved}</td>
                      <td className={styles.rejected}>{sp.rejected}</td>
                      <td>
                        <span
                          className={`${styles.rateBadge} ${
                            sp.approval_rate >= 70 ? styles.rateGood :
                            sp.approval_rate >= 40 ? styles.rateWarn :
                            styles.rateBad
                          }`}
                        >
                          {formatPct(sp.approval_rate)}
                        </span>
                      </td>
                      <td>{formatHours(sp.avg_time_to_action_hours)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Empty state */}
          {overview && overview.total_detected === 0 && (
            <div className={styles.emptyState}>
              <p>No LLM-detected orders found in this time period.</p>
              <p className={styles.emptyHint}>
                Orders are automatically detected when the email classification system identifies order-related emails.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function OutcomeBar({
  label,
  count,
  total,
  color,
}: {
  label: string;
  count: number;
  total: number;
  color: string;
}) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className={styles.outcomeRow}>
      <div className={styles.outcomeLabel}>{label}</div>
      <div className={styles.outcomeBarTrack}>
        <div
          className={styles.outcomeBarFill}
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
      <div className={styles.outcomeValue}>
        {count} <span className={styles.outcomePct}>({pct.toFixed(0)}%)</span>
      </div>
    </div>
  );
}
